import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { imagenPequena } from '@plataforma/web/MiCuenta';
import { usePuede } from '../permisos';

interface Negocio {
  razonSocial: string | null;
  rfc: string | null;
  regimenFiscal: string | null;
  domicilioFiscal: Record<string, string> | null;
  telefono: string | null;
  correoContacto: string | null;
  horarios: string | null;
  redes: Record<string, string>;
  padronImportadores: {
    inscrito: boolean;
    folio: string | null;
    fechaAlta: string | null;
    sectorAutomotriz: boolean;
  } | null;
  logo: string | null;
}

const REDES = ['web', 'facebook', 'instagram', 'tiktok', 'youtube'] as const;

/** Mi negocio (arquitectura 2.12): datos fiscales y de contacto del lotero; los edita el dueño. */
export function MiNegocio() {
  const api = useApi();
  const puede = usePuede();
  const editable = puede('lotero.administrar');
  const n = useConsulta<Negocio>('/platform/mi-lotero/negocio');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const hacer = async (fn: () => Promise<unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensaje);
      n.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const guardar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const v = (k: string) => String(d.get(k) ?? '').trim() || null;
    const cp = v('codigoPostal');
    void hacer(
      () =>
        api('PUT', '/platform/mi-lotero/negocio', {
          razonSocial: v('razonSocial'),
          rfc: v('rfc'),
          regimenFiscal: v('regimenFiscal'),
          domicilioFiscal: cp
            ? {
                calle: v('calle') ?? '',
                colonia: v('colonia') ?? '',
                municipio: v('municipio') ?? '',
                estado: v('estado') ?? '',
                codigoPostal: cp,
              }
            : null,
          telefono: v('telefono'),
          correoContacto: v('correoContacto'),
          horarios: v('horarios'),
          redes: Object.fromEntries(REDES.map((r) => [r, v(`red_${r}`)]).filter(([, x]) => x)),
          padronImportadores:
            d.get('padronInscrito') === ''
              ? null
              : {
                  inscrito: d.get('padronInscrito') === 'si',
                  folio: v('padronFolio'),
                  fechaAlta: v('padronFecha'),
                  sectorAutomotriz: d.get('padronSector') === 'on',
                },
        }),
      'Guardado',
    );
  };
  const subirLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (archivo)
      void hacer(
        async () =>
          api('PUT', '/platform/mi-lotero/negocio/logo', await imagenPequena(archivo, 512)),
        'Logo actualizado',
      );
  };
  const x = n.datos;
  const campo = (
    nombre: string,
    etiqueta: string,
    valor: string | null | undefined,
    extra: object = {},
  ) => (
    <label className="campo">
      <span>{etiqueta}</span>
      <input name={nombre} defaultValue={valor ?? ''} disabled={!editable} {...extra} />
    </label>
  );
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mi negocio</h1>
          <div className="subtitulo">
            Datos fiscales y de contacto del lotero{editable ? '' : ' (solo el dueño los cambia)'}
          </div>
        </div>
      </div>
      {n.error && <p className="error">{n.error}</p>}
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      {x && (
        <>
          <section
            className="seccion cuerpo"
            style={{ display: 'flex', gap: 16, alignItems: 'center' }}
          >
            {x.logo ? (
              <img src={x.logo} alt="Logo" style={{ maxHeight: 72, maxWidth: 200 }} />
            ) : (
              <span className="subtitulo">Sin logo</span>
            )}
            {editable && (
              <label className="boton">
                Cambiar logo
                <input type="file" accept="image/*" hidden onChange={subirLogo} />
              </label>
            )}
          </section>
          <form className="seccion cuerpo formulario" onSubmit={guardar}>
            <h2>Datos fiscales</h2>
            {campo('razonSocial', 'Razón social', x.razonSocial, { maxLength: 200 })}
            {campo('rfc', 'RFC', x.rfc, { maxLength: 13, style: { textTransform: 'uppercase' } })}
            {campo('regimenFiscal', 'Régimen fiscal', x.regimenFiscal, { maxLength: 120 })}
            {campo('calle', 'Calle y número', x.domicilioFiscal?.['calle'])}
            {campo('colonia', 'Colonia', x.domicilioFiscal?.['colonia'])}
            {campo('municipio', 'Municipio', x.domicilioFiscal?.['municipio'])}
            {campo('estado', 'Estado', x.domicilioFiscal?.['estado'])}
            {campo('codigoPostal', 'Código postal', x.domicilioFiscal?.['codigoPostal'], {
              pattern: '\\d{5}',
            })}
            <h2>Padrón de Importadores</h2>
            <label className="campo">
              <span>¿Inscrito en el Padrón de Importadores?</span>
              <select
                name="padronInscrito"
                defaultValue={
                  x.padronImportadores ? (x.padronImportadores.inscrito ? 'si' : 'no') : ''
                }
                disabled={!editable}
              >
                <option value="">Sin indicar</option>
                <option value="si">Sí, importo a mi nombre</option>
                <option value="no">No, importo por el padrón de mi agente</option>
              </select>
            </label>
            {campo('padronFolio', 'Folio del padrón', x.padronImportadores?.folio, {
              maxLength: 40,
            })}
            {campo('padronFecha', 'Fecha de alta', x.padronImportadores?.fechaAlta, {
              type: 'date',
            })}
            <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
              <input
                name="padronSector"
                type="checkbox"
                defaultChecked={x.padronImportadores?.sectorAutomotriz ?? false}
                disabled={!editable}
                style={{ minHeight: 0 }}
              />
              <span>Sector automotriz (padrón de sectores específicos)</span>
            </label>
            <h2>Contacto</h2>
            {campo('telefono', 'Teléfono', x.telefono, { inputMode: 'tel' })}
            {campo('correoContacto', 'Correo de contacto', x.correoContacto, { type: 'email' })}
            {campo('horarios', 'Horarios', x.horarios, { maxLength: 300 })}
            {REDES.map((r) => (
              <div key={r}>
                {campo(
                  `red_${r}`,
                  r === 'web' ? 'Sitio web' : r[0]?.toUpperCase() + r.slice(1),
                  x.redes[r],
                  { type: 'url' },
                )}
              </div>
            ))}
            {editable && (
              <div>
                <button className="boton primario">Guardar</button>
              </div>
            )}
          </form>
        </>
      )}
    </>
  );
}
